import React, {useEffect, useState} from "react";
import {Button, Card, Col, Container, CustomInput, Input, Row, Spinner,} from "reactstrap";
import {fetchCandidateConfig, updateCandidateConfig} from "util/Helper";
import Swal from "sweetalert2";

const CandidateFlowConfig = () => {
    const [loading, setLoading] = useState(true); // Loading state
    const [basicInfo, setBasicInfoData] = useState({
        enabled: true,
        fields: {
            name: {enabled: true, label: "Full Name"},
            partnerName: {enabled: true, label: "Partner's Name"},
            dob: {enabled: false, label: "Date of Birth"},
            email: {enabled: true, label: "Email Address"},
            phone: {enabled: true, label: "Phone Number"},
            altPhone: {enabled: false, label: "Alternate Phone Number"},
            currentAddress: {enabled: true, label: "Current Address"},
            city: {enabled: true, label: "City"},
            homeTown: {enabled: true, label: "Home Town"},
            cnic: {enabled: false, label: "CNIC"},
            gender: {enabled: true, label: "Gender"},
            nationality: {enabled: true, label: "Nationality"},
        },
    });

    const [education, setEducationData] = useState({
        enabled: true,
        fields: {
            degree: {enabled: true, label: "Degree"},
            institute: {enabled: true, label: "Institute"},
            cgpa: {enabled: false, label: "CGPA"},
            specialization: {enabled: true, label: "Specialization"},
            graduationYear: {enabled: true, label: "Graduation Year"},
            qualification: {enabled: true, label: "Qualification"},
            resume: {enabled: false, label: "Resume"},
        },
    });

    const [experience, setExperienceData] = useState({
        enabled: true,
        fields: {
            internshipDetails: {enabled: true, label: "Internship Details"},
            yearsOfExperience: {enabled: true, label: "Years of Experience"},
            companyName: {enabled: true, label: "Company Name"},
            department: {enabled: false, label: "Department"},
            designation: {enabled: true, label: "Designation"},
            currentEmployer: {enabled: false, label: "Current Employer"},
        },
    });

    const [candidateSpecific, setCandidateSpecificData] = useState({
        enabled: true,
        fields: {
            drivingLicenseValidity: {
                enabled: true,
                label: "Driving License Validity",
            },
            drivingLicenseFile: {enabled: false, label: "Driving License File"},
            talentProgram: {enabled: true, label: "Talent Program"},
            talentProgramSource: {enabled: true, label: "Talent Program Source"},
            personality: {enabled: true, label: "Personality"},
        },
    });

    const [orgQuestions, setOrgQuestionsData] = useState({
        enabled: true,
        fields: {
            relativesCount: {enabled: true, label: "Relatives Count"},
            relativeDetails: {enabled: false, label: "Relative Details"},
            relationshipType: {enabled: true, label: "Relationship Type"},
            pastInterviews: {enabled: true, label: "Past Interviews"},
            pastInterviewDetails: {enabled: false, label: "Past Interview Details"},
        },
    });

    const [isDirty, setIsDirty] = useState(false);
    const [editingField, setEditingField] = useState({
        section: null,
        key: null,
    });
    const [labelInput, setLabelInput] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchCandidateConfig("util", "candidate_config");
                if (data) {
                    setBasicInfoData({
                        enabled: data.basicInfo?.enabled ?? true,
                        fields: data.basicInfo?.fields ?? {},
                    });
                    setEducationData({
                        enabled: data.education?.enabled ?? true,
                        fields: data.education?.fields ?? {},
                    });
                    setExperienceData({
                        enabled: data.experience?.enabled ?? true,
                        fields: data.experience?.fields ?? {},
                    });
                    setCandidateSpecificData({
                        enabled: data.candidateSpecific?.enabled ?? true,
                        fields: data.candidateSpecific?.fields ?? {},
                    });
                    setOrgQuestionsData({
                        enabled: data.orgQuestions?.enabled ?? true,
                        fields: data.orgQuestions?.fields ?? {},
                    });
                }
            } catch (error) {
                console.error("Error fetching candidate config:", error);
            } finally {
                setLoading(false); // Set loading to false when data is fetched
            }
        };
        fetchData();
    }, []);

    const toggleEnabled = (section, key) => {
        const setData = (data, setData) => {
            setData((prevState) => {
                if (key === "enabled") {
                    setIsDirty(true);
                    return {...prevState, enabled: !prevState.enabled};
                }
                setIsDirty(true);
                return {
                    ...prevState,
                    fields: {
                        ...prevState.fields,
                        [key]: {
                            ...prevState.fields[key],
                            enabled: !prevState.fields[key].enabled,
                        },
                    },
                };
            });
        };

        switch (section) {
            case "basicInfo":
                setData(basicInfo, setBasicInfoData);
                break;
            case "education":
                setData(education, setEducationData);
                break;
            case "experience":
                setData(experience, setExperienceData);
                break;
            case "candidateSpecific":
                setData(candidateSpecific, setCandidateSpecificData);
                break;
            case "orgQuestions":
                setData(orgQuestions, setOrgQuestionsData);
                break;
            default:
                break;
        }
    };

    const handleEditClick = (section, key, currentLabel) => {
        setEditingField({section, key});
        setLabelInput(currentLabel);
    };

    const handleLabelChange = (event) => setLabelInput(event.target.value);

    const handleCancel = () => setEditingField({section: null, key: null});

    const handleDone = () => {
        const {section, key} = editingField;
        const updateFieldLabel = (sectionData, setSectionData) => {
            setSectionData((prevState) => {
                const updatedFields = {
                    ...prevState.fields,
                    [key]: {
                        ...prevState.fields[key],
                        label: labelInput,
                    },
                };
                return {...prevState, fields: updatedFields};
            });
        };

        switch (section) {
            case "basicInfo":
                updateFieldLabel(basicInfo, setBasicInfoData);
                break;
            case "education":
                updateFieldLabel(education, setEducationData);
                break;
            case "experience":
                updateFieldLabel(experience, setExperienceData);
                break;
            case "candidateSpecific":
                updateFieldLabel(candidateSpecific, setCandidateSpecificData);
                break;
            case "orgQuestions":
                updateFieldLabel(orgQuestions, setOrgQuestionsData);
                break;
            default:
                break;
        }

        setEditingField({section: null, key: null});
        setIsDirty(true);
    };

    const updateFirestoreDocument = async () => {
        const candidateConfigData = {
            basicInfo,
            education,
            experience,
            candidateSpecific,
            orgQuestions,
        };

        const success = await updateCandidateConfig(
            "util",
            "candidate_config",
            candidateConfigData
        );
        if (success) {
            setIsDirty(false);
            Swal.fire({
                title: "Success!",
                text: "Document updated successfully!",
                icon: "success",
                confirmButtonText: "OK",
            });
        } else {
            Swal.fire({
                title: "Failure!",
                text: "Document update failed!",
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    if (loading) {
        return (
            <Container
                className="d-flex justify-content-center align-items-center"
                style={{height: "100vh"}}
            >
                <Spinner color="primary"/>
            </Container>
        );
    }

    return (
        <Container fluid>
            <div className="d-flex justify-content-end m-2">
                <Button
                    color="primary"
                    onClick={updateFirestoreDocument}
                    disabled={!isDirty}
                >
                    Update
                </Button>
            </div>
            {[
                "basicInfo",
                "education",
                "experience",
                "candidateSpecific",
                "orgQuestions",
            ].map((section) => (
                <Card key={section} className="m-2">
                    <div className="d-flex align-items-center p-2">
            <span className="mr-3">
              {section
                  .replace(/([A-Z])/g, " $1")
                  .replace(/\b\w/g, (char) => char.toUpperCase())}
            </span>
                        <CustomInput
                            type="switch"
                            id={`${section}Enabled`}
                            checked={eval(section).enabled}
                            onChange={() => toggleEnabled(section, "enabled")}
                        />
                    </div>
                    <Row className="d-flex flex-wrap m-4 w-[120]">
                        {eval(section).enabled &&
                            Object.entries(eval(section).fields).map(([key, value]) => (
                                <Col
                                    md="3"
                                    key={key}
                                    className="d-flex align-items-center my-2 justify-content-between"
                                >
                                    {editingField.section === section &&
                                    editingField.key === key ? (
                                        <>
                                            <Input
                                                value={labelInput}
                                                onChange={handleLabelChange}
                                                className="mr-2"
                                            />
                                            <Button color="primary" size="sm" onClick={handleDone}>
                                                <i className="fas fa-check"/>
                                            </Button>
                                            <Button color="danger" size="sm" onClick={handleCancel}>
                                                <i className="fas fa-ban"/>
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="mr-3">{value.label}</span>
                                            <div className="d-flex ">
                                                <CustomInput
                                                    type="switch"
                                                    id={`${section}-${key}`}
                                                    checked={value.enabled}
                                                    onChange={() => toggleEnabled(section, key)}
                                                />
                                                <Button
                                                    color="link"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleEditClick(section, key, value.label)
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </Col>
                            ))}
                    </Row>
                </Card>
            ))}
        </Container>
    );
};

export default CandidateFlowConfig;
